import { useState, type FormEvent } from 'react'
import { Alert, AppLayout, Badge, Button, Card, Field, RequireSession, type CurrentUser } from '@jmvilaca-corp/auth-ui'
import {
  accessLabels,
  roleLabels,
  useCalendars,
  useCreateCalendar,
  useDeleteCalendar,
  useShareCalendar,
  useShares,
  useUnshareCalendar,
  useUpdateCalendar,
  type CalendarAccess,
  type CalendarSummary,
} from '../lib/calendars'
import { standardSource, useCalendarSources } from '../lib/connectors'
import ConnectorSignInDialog from '../components/ConnectorSignInDialog'

const palette = ['#2563EB', '#16A34A', '#DC2626', '#D97706', '#7C3AED', '#DB2777', '#0891B2', '#525252']

const selectClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-100'

export default function CalendarsPage() {
  return (
    <RequireSession>
      {(user) => (
        <AppLayout user={user}>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Mes calendriers
          </h1>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Un calendrier se partage avec un autre utilisateur en lecture seule (il voit les evenements) ou en
            controle total (il peut aussi en ajouter, en modifier et en supprimer).
          </p>
          <Calendars user={user} />
        </AppLayout>
      )}
    </RequireSession>
  )
}

function Calendars({ user }: { user: CurrentUser }) {
  const calendars = useCalendars()

  if (calendars.isPending) {
    return <p className="mt-6 text-sm text-neutral-500">Chargement...</p>
  }

  if (calendars.isError) {
    return (
      <div className="mt-6">
        <Alert>{calendars.error.message}</Alert>
      </div>
    )
  }

  const mine = calendars.data.filter((calendar) => calendar.role === 'owner')
  const shared = calendars.data.filter((calendar) => calendar.role !== 'owner')

  return (
    <div className="mt-6 space-y-6">
      <Card title="Nouveau calendrier">
        <NewCalendarForm />
      </Card>

      {mine.length === 0 ? (
        <Card>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Vous n'avez pas encore de calendrier.</p>
        </Card>
      ) : (
        mine.map((calendar) => <OwnedCalendar key={calendar.calendarId} calendar={calendar} />)
      )}

      {shared.length > 0 && (
        <Card title="Partages avec moi">
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {shared.map((calendar) => (
              <SharedCalendar key={calendar.calendarId} calendar={calendar} userId={user.userId} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  return (
    <div role="radiogroup" aria-label="Couleur" className="flex flex-wrap gap-2">
      {palette.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={`Couleur ${color}`}
          onClick={() => onChange(color)}
          className={`h-7 w-7 rounded-full transition ${
            value === color ? 'ring-2 ring-neutral-900 ring-offset-2 dark:ring-neutral-100 dark:ring-offset-neutral-900' : ''
          }`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  )
}

export function NewCalendarForm() {
  const create = useCreateCalendar()
  const sources = useCalendarSources()
  const [name, setName] = useState('')
  const [color, setColor] = useState(palette[0]!)
  const [sourceKey, setSourceKey] = useState(standardSource)
  // Type choisi mais pas encore retenu : son compte reste a lier.
  const [signingInto, setSigningInto] = useState<string | null>(null)

  const source = sources.data?.find((item) => item.key === sourceKey)
  const pending = sources.data?.find((item) => item.key === signingInto)

  const onSourceChange = (key: string) => {
    const chosen = sources.data?.find((item) => item.key === key)
    if (chosen !== undefined && chosen.requiresAccount && chosen.login === null) {
      setSigningInto(key)
    } else {
      setSourceKey(key)
    }
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate(
      { name: name.trim(), color, source: sourceKey },
      {
        onSuccess: () => {
          setName('')
          setSourceKey(standardSource)
        },
      },
    )
  }

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-4">
        {create.isError && <Alert>{create.error.message}</Alert>}
        <Field
          label="Nom"
          value={name}
          maxLength={80}
          placeholder="Famille, Travail, Club de sport..."
          onChange={(event) => setName(event.target.value)}
        />
        <ColorPicker value={color} onChange={setColor} />
        <div>
          <label className="block">
            <span className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">Type de calendrier</span>
            <select
              value={sourceKey}
              disabled={!sources.isSuccess}
              onChange={(event) => onSourceChange(event.target.value)}
              className={`mt-1.5 w-full sm:w-72 ${selectClass}`}
            >
              {sources.isSuccess ? (
                sources.data.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))
              ) : (
                <option value={standardSource}>Calendrier standard</option>
              )}
            </select>
          </label>
          {source !== undefined && source.requiresAccount && source.login !== null && (
            <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
              Lie au compte {source.label} {source.login}.
            </p>
          )}
        </div>
        {sources.isError && <Alert>{sources.error.message}</Alert>}
        <Button type="submit" size="sm" disabled={create.isPending || name.trim().length === 0}>
          {create.isPending ? 'Creation...' : 'Creer le calendrier'}
        </Button>
      </form>
      {pending !== undefined && (
        <ConnectorSignInDialog
          source={pending}
          onLinked={() => {
            setSourceKey(pending.key)
            setSigningInto(null)
          }}
          // Sans compte lie, le calendrier reste du type precedent.
          onClose={() => setSigningInto(null)}
        />
      )}
    </>
  )
}

function OwnedCalendar({ calendar }: { calendar: CalendarSummary }) {
  const update = useUpdateCalendar()
  const remove = useDeleteCalendar()
  const [editing, setEditing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [name, setName] = useState(calendar.name)
  const [color, setColor] = useState(calendar.color)

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    update.mutate({ calendarId: calendar.calendarId, name: name.trim(), color }, { onSuccess: () => setEditing(false) })
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: calendar.color }} aria-hidden="true" />
          <h2 className="truncate text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            {calendar.name}
          </h2>
          {calendar.source !== standardSource && <SourceBadge source={calendar.source} />}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setEditing((previous) => !previous)}>
            {editing ? 'Annuler' : 'Modifier'}
          </Button>
          {confirmingDelete ? (
            <>
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate(calendar.calendarId)}
              >
                Confirmer
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                Annuler
              </Button>
            </>
          ) : (
            <Button type="button" variant="danger" size="sm" onClick={() => setConfirmingDelete(true)}>
              Supprimer
            </Button>
          )}
        </div>
      </div>

      {confirmingDelete && (
        <p className="mt-3 text-sm text-red-700 dark:text-red-300">
          Le calendrier, tous ses evenements et ses partages seront supprimes definitivement.
        </p>
      )}
      {remove.isError && (
        <div className="mt-3">
          <Alert>{remove.error.message}</Alert>
        </div>
      )}

      {editing && (
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          {update.isError && <Alert>{update.error.message}</Alert>}
          <Field label="Nom" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
          <ColorPicker value={color} onChange={setColor} />
          <Button type="submit" size="sm" disabled={update.isPending || name.trim().length === 0}>
            Enregistrer
          </Button>
        </form>
      )}

      <Shares calendar={calendar} />
    </Card>
  )
}

function SourceBadge({ source }: { source: string }) {
  const sources = useCalendarSources()

  return <Badge tone="neutral">{sources.data?.find((item) => item.key === source)?.label ?? source}</Badge>
}

function Shares({ calendar }: { calendar: CalendarSummary }) {
  const shares = useShares(calendar.calendarId)
  const share = useShareCalendar(calendar.calendarId)
  const unshare = useUnshareCalendar(calendar.calendarId)
  const [email, setEmail] = useState('')
  const [access, setAccess] = useState<CalendarAccess>('readOnly')

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    share.mutate({ email: email.trim(), access }, { onSuccess: () => setEmail('') })
  }

  return (
    <div className="mt-5 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200">Partages</h3>

      {shares.isError && (
        <div className="mt-2">
          <Alert>{shares.error.message}</Alert>
        </div>
      )}

      {shares.isSuccess && shares.data.length === 0 && (
        <p className="mt-2 text-sm text-neutral-500">Ce calendrier n'est partage avec personne.</p>
      )}

      {shares.isSuccess && shares.data.length > 0 && (
        <ul className="mt-2 divide-y divide-neutral-200 dark:divide-neutral-800">
          {shares.data.map((item) => (
            <li key={item.userId} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <div className="min-w-0 text-sm">
                <div className="truncate font-medium text-neutral-900 dark:text-neutral-100">{item.displayName}</div>
                <div className="truncate text-neutral-600 dark:text-neutral-400">{item.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  aria-label={`Droit de ${item.email}`}
                  value={item.access}
                  disabled={share.isPending}
                  onChange={(event) => share.mutate({ email: item.email, access: event.target.value as CalendarAccess })}
                  className={selectClass}
                >
                  <option value="readOnly">{accessLabels.readOnly}</option>
                  <option value="fullControl">{accessLabels.fullControl}</option>
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={unshare.isPending}
                  onClick={() => unshare.mutate(item.userId, { onSuccess: () => void shares.refetch() })}
                >
                  Retirer
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <Field
            label="Partager avec"
            type="email"
            value={email}
            placeholder="adresse e-mail d'un utilisateur"
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <select
          aria-label="Droit accorde"
          value={access}
          onChange={(event) => setAccess(event.target.value as CalendarAccess)}
          className={selectClass}
        >
          <option value="readOnly">{accessLabels.readOnly}</option>
          <option value="fullControl">{accessLabels.fullControl}</option>
        </select>
        <Button type="submit" size="sm" disabled={share.isPending || email.trim().length === 0}>
          Partager
        </Button>
      </form>
      {share.isError && (
        <div className="mt-2">
          <Alert>{share.error.message}</Alert>
        </div>
      )}
    </div>
  )
}

function SharedCalendar({ calendar, userId }: { calendar: CalendarSummary; userId: string }) {
  const unshare = useUnshareCalendar(calendar.calendarId)

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: calendar.color }} aria-hidden="true" />
        <div className="min-w-0 text-sm">
          <div className="truncate font-medium text-neutral-900 dark:text-neutral-100">{calendar.name}</div>
          <div className="truncate text-neutral-600 dark:text-neutral-400">
            Partage par {calendar.ownerName ?? calendar.ownerEmail ?? 'un utilisateur'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge tone={calendar.role === 'fullControl' ? 'ok' : 'neutral'}>{roleLabels[calendar.role]}</Badge>
        <Button type="button" variant="outline" size="sm" disabled={unshare.isPending} onClick={() => unshare.mutate(userId)}>
          Quitter
        </Button>
      </div>
    </li>
  )
}
